import * as React from 'react'
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DnsIcon from '@mui/icons-material/Dns';
import { assetsApi, defaultApi, inspectionsApi, reportsApi } from '../../api-interface';
import { AnalysisResponse, AssetResponse, ValidationsResponse } from '../../api';
import { Typography, Tooltip, Alert, Box, useTheme, Button, Modal, TextField, CircularProgress, Grid } from '@mui/material';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import HelpIcon from '@mui/icons-material/Help';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AssetInfoChip from '../Assets/AssetInfoChip';
import AnalyseFile from './AnalyseFile/AnalyseFile';
import { TenantContext } from '../..';
import { useSnackbar } from 'notistack';
import DeleteIcon from '@mui/icons-material/Delete'
import { GridRowsProp, GridColDef, getGridSingleSelectOperators, GridFilterModel, GridFilterItem } from '@mui/x-data-grid';
import StyledDataGrid, { stringFilterOperators } from '../../components/common/StyledDataGrid/StyledDataGrid';


export async function loader(tenant_id) {
  const inspections = await inspectionsApi(tenant_id).getAnalysesInspectionsGet();
  const user = await defaultApi.readUsersMeMeGet();
  // Extract contractor feature flag
  const useContractor = user.data.tenants.find((x) => x.id === tenant_id)?.feature_flag_contractors || false;
  const ret = { "inspections": inspections.data, "contractor_flag": useContractor };
  return ret;
}

const Inspections = () => {
  const loaded_data = useLoaderData();
  const inspections: AnalysisResponse[] = loaded_data["inspections"];
  const contractor_flag = loaded_data["contractor_flag"] as boolean;
  const inspections_missing_assets = inspections.filter((inspection) => (inspection.asset_add_1 === null)).length > 0
  const theme = useTheme()
  const tenant_id = React.useContext(TenantContext)
  const totalCompliant = inspections.filter((inspection) => (inspection.analysis.outcome == "pass")).length
  const totalWarning = inspections.filter((inspection) => (inspection.analysis.outcome == "warning")).length
  const totalNonCompliant = inspections.filter((inspection) => (inspection.analysis.outcome == "fail")).length
  const [modalOpen, setModalOpen] = React.useState(false)
  const [reportModalOpen, setReportModalOpen] = React.useState(false)
  const [reportStartDate, setReportStartDate] = React.useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  })
  const [reportEndDate, setReportEndDate] = React.useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  })
  const [reportLoading, setReportLoading] = React.useState(false)
  const [reportError, setReportError] = React.useState<string | null>(null);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const engineerFilter = searchParams.get("engineer");
  const compliantFilter = searchParams.get("compliant");


  let items: GridFilterItem[] = [];
  if (engineerFilter) {
    items.push({ field: 'engineer', operator: 'equals', value: engineerFilter });
  }
  if (compliantFilter) {
    items.push({
      field: 'outcome',
      operator: 'is',
      value: compliantFilter === 'pass' ? 'Compliant' : compliantFilter === 'warning' ? 'Warning' : 'Needs Attention'
    });
  }

  // Get complaint filter from search params
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: items
  });

  // Sync filterModel with searchParams
  React.useEffect(() => {
    setFilterModel({
      items: items
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compliantFilter, engineerFilter]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    minWidth: 80,
    borderRadius: 4,
  };

  const inspectionRow = (inspection: AnalysisResponse) => {
    return {
      id: inspection.analysis.id,
      asset_address_1: inspection.asset_add_1 ? String(inspection.asset_add_1) : null,
      inspection_date: inspection.analysis.inspection_date ? new Date(String(inspection.analysis.inspection_date)) : null,
      engineer: inspection.analysis.engineer_name ? String(inspection.analysis.engineer_name) : "",
      next_inspection_due: inspection.analysis.next_inspection_due ? new Date(String(inspection.analysis.next_inspection_due)) : null,
      outcome: inspection.analysis.outcome == "pass" ? "Compliant" : inspection.analysis.outcome == "warning" ? "Warning" : "Needs Attention",
      asset_id: inspection.asset_id,
      analysis_id: inspection.analysis.id,
      contractor_name: inspection.contractor_name ? String(inspection.contractor_name) : "",
    }
  }

  let columns: GridColDef[] = [
    {
      field: 'asset_address_1', headerName: 'Asset Address Line 1', flex: 1, minWidth: 170, headerClassName: 'table-header', filterOperators: stringFilterOperators, renderCell: (params) => {
        return params.value ? params.value : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    },
    {
      field: 'inspection_date', headerName: 'LGSR Inspection Date', flex: 1, minWidth: 150, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    { field: 'engineer', headerName: 'Engineer', flex: 1, minWidth: 150, headerClassName: 'table-header', filterOperators: stringFilterOperators },

    {
      field: 'next_inspection_due', headerName: 'Next LGSR Inspection Due', headerClassName: 'table-header', flex: 1, minWidth: 150, type: 'date', renderCell: (params) => {
        const value = params.value as Date | null;
        const isOverdue = value ? value < new Date() : false;
        return value
          ? (
            <Typography
              sx={{ color: isOverdue ? theme.palette.error.main : undefined, verticalAlign: 'middle', display: 'inline' }}
            >
              {value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          )
          : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }} />;
      }
    },
    {
      field: 'outcome', headerName: 'LGSR Outcome', flex: 1, minWidth: 170, headerClassName: 'table-header', type: 'singleSelect', valueOptions: ['Compliant', 'Warning', 'Needs Attention'], filterOperators: getGridSingleSelectOperators(), renderCell: (params) => {
        return params.value === "Compliant" ?
          <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.success.main}><TaskAltIcon fontSize="inherit" /> Compliant</Typography>
          : params.value === "Warning" ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.warning.main}><ErrorOutlineIcon fontSize="inherit" /> Warning</Typography>
            : <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.error.main}><ErrorOutlineIcon fontSize="inherit" /> Needs Attention</Typography>
      }, sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const order = ['Compliant', 'Warning', 'Needs Attention'];
        return order.indexOf(v1) - order.indexOf(v2);
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <UnstyledLink to={params.row.analysis_id + "/validations"}><Tooltip title="Verifications"><ChecklistIcon sx={{ verticalAlign: 'middle' }} /></Tooltip></UnstyledLink>,
        params.row.asset_id !== null ? <UnstyledLink to={"/dashboard/" + tenant_id + "/assets/" + params.row.asset_id}><Tooltip title="Go to asset"><DnsIcon sx={{ ml: 1, verticalAlign: 'middle' }} /></Tooltip></UnstyledLink> : <></>,
        <Tooltip title="Delete LGSR Inspection">
          <DeleteIcon onClick={async () => {
            if (window.confirm("Are you sure you want to remove this inspection?")) {
              try {
                await inspectionsApi(tenant_id).deleteAnalysisInspectionsAnalysisIdDelete(params.row.id)
                enqueueSnackbar("LGSR Inspection deleted", { variant: "success" })
              } catch (e) {
                enqueueSnackbar("Error deleting inspection: " + e, { variant: "error" })
              }

            }
            navigate("")
          }} sx={{ ml: 1, verticalAlign: 'middle', cursor: 'pointer' }} />
        </Tooltip>
      ],
    }
  ];

  if (contractor_flag) {
    // Insert contractor_name column after the engineer column
    const engineerColIndex = columns.findIndex(col => col.field === 'engineer');
    columns.splice(engineerColIndex + 1, 0, {
      field: 'contractor_name',
      headerName: 'Contractor',
      flex: 1,
      minWidth: 100,
      headerClassName: 'table-header',
      filterOperators: stringFilterOperators,
      renderCell: (params) => {
        return params.value ? params.value : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    });
  }

  const rows: GridRowsProp = inspections.map(inspectionRow);

  const handleGetReport = async () => {
    setReportError(null);
    if (reportStartDate > reportEndDate) {
      setReportError('Start date must be before end date.');
      return;
    }
    setReportLoading(true);
    try {
      // Ensure dates are passed as YYYY-MM-DD
      const formattedStartDate = new Date(reportStartDate).toISOString().slice(0, 10);
      const formattedEndDate = new Date(reportEndDate).toISOString().slice(0, 10);
      const response = await reportsApi(tenant_id).inspectionsReportReportsInspectionsGet(
        formattedStartDate,
        formattedEndDate,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      let filename = 'inspections_report.pdf';
      const disposition = response.headers?.['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (e) {
      setReportError('Failed to download report.');
    }
    setReportLoading(false);
    setReportModalOpen(false);
  }

  return (
    <Box display="flex" flexDirection={"column"} gap={5}>
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent={"space-between"}>
        <Typography variant="h4" > LGSR Inspections </Typography>
        <Box display="flex" flexDirection="row" gap={2} >
          <Button variant="contained" onClick={() => { setReportModalOpen(true) }}>Create Report</Button>
          <Button variant="contained" onClick={() => { setModalOpen(true) }}>Upload</Button>
        </Box>
        <Modal
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
        >
          <Box sx={style} display="flex" flexDirection="column" gap={3} p={3}>
            <Typography variant="h6">Create Report</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the inspection date range for your report:
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} width="100%">
              <TextField
                label="LGSR Inspection Start Date"
                type="date"
                value={reportStartDate}
                onChange={e => setReportStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="LGSR Inspection End Date"
                type="date"
                value={reportEndDate}
                onChange={e => setReportEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            {reportError && <Typography color="error" variant="body2">{reportError}</Typography>}
            <Button variant="contained" onClick={handleGetReport} disabled={reportLoading}>
              {reportLoading ? <CircularProgress size={24} color="inherit" /> : 'Get Report'}
            </Button>
          </Box>
        </Modal>
        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
          }}
        >
          <Box sx={style} display={"flex"} justifyContent={"space-around"}>
            <AnalyseFile closeModal={() => { setModalOpen(false) }}></AnalyseFile>
          </Box>
        </Modal>
      </Box>

      {inspections_missing_assets && <Alert variant='standard' sx={{ borderColor: theme.palette.warning.dark, borderWidth: "1px", borderStyle: "solid" }} severity="warning">Some inspections have not been able to be associated with an asset</Alert>}
      <Grid container spacing={2}>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'pass' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Compliant' }] })
          }}>
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Compliant LGSR Inspections</Typography>}
            right={<Typography whiteSpace={"nowrap"} fontSize={"1.25rem"} color={theme.palette.success.main} fontWeight={"bold"}>
              <TaskAltIcon fontSize='inherit' /> {totalCompliant}
            </Typography>}
            bgcolor={theme.palette.successBackground.main}
            borderColor={theme.palette.successOutline.main}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'warning' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Warning' }] })
          }}>
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>LGSR Inspections With Warnings</Typography>}
            right={<Typography whiteSpace={"nowrap"} fontSize={"1.25rem"} color={theme.palette.warning.main} fontWeight={"bold"}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalWarning}
            </Typography>}
            bgcolor={theme.palette.warningBackground.main}
            borderColor={theme.palette.warningOutline.main}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({ 'compliant': 'fail' });
            setFilterModel({ items: [{ field: 'outcome', operator: 'is', value: 'Needs Attention' }] })
          }}>
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>LGSR Inspections Requiring Attention</Typography>}
            right={<Typography whiteSpace={"nowrap"} fontSize={"1.25rem"} color={theme.palette.error.main} fontWeight={"bold"}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalNonCompliant}
            </Typography>}
            bgcolor={theme.palette.errorBackground.main}
            borderColor={theme.palette.errorOutline.main}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => {
            setSearchParams({});
            setFilterModel({ items: [] })
          }}>
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Total LGSR Inspections</Typography>}
            right={<Typography whiteSpace={"nowrap"} fontSize={"1.25rem"} color={theme.palette.primary.main} fontWeight={"bold"}>
              <ErrorOutlineIcon fontSize='inherit' /> {totalCompliant + totalWarning + totalNonCompliant}
            </Typography>}
            bgcolor={theme.palette.primary.light}
            borderColor={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      <Box sx={{
        // mt: 5,
        width: '100%'
      }} >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid rows={rows} columns={columns} showToolbar filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => {
              setFilterModel(newFilterModel)
            }} />
        </div>
      </Box>
    </Box >
  )
}

export default Inspections